import React, { useState, useCallback } from 'react';
import { Card, Spinner, Button, Form } from 'react-bootstrap';
import { Search, Star, Copy, Clipboard, Clock } from 'lucide-react';
import type { GpKeywordCpSearch, CoupangProduct } from '../../../../models/Coupang';
import { ShippingType } from '../../../../models/Coupang';
import {
  productAttributesClient,
  type ProductAttributeUpdate,
} from '../../../../infrastructure/ProductAttributesClient';
import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';
import '../../matching/components/CoupangProductsTable.css';
import { buildCoupangProductUrl } from './coupangUrl';

// Utility function to format prices with backward compatibility
const formatPrice = (product: CoupangProduct): string => {
  // New structure: priceInfo.salePrice is a string like "14,540원"
  if (product.priceInfo?.salePrice) {
    return product.priceInfo.salePrice;
  }

  // Legacy structure: salePrice or unitPrice as numbers
  const legacyProduct = product as any;
  if (legacyProduct.salePrice) {
    return legacyProduct.salePrice.toLocaleString() + '원';
  }
  if (legacyProduct.unitPrice) {
    return legacyProduct.unitPrice.toLocaleString() + '원';
  }

  return 'N/A';
};

// Helper function to calculate delivery days from Korean delivery descriptions
const calculateDeliveryDays = (deliveryDescription: string, scrapedAt?: string): number | null => {
  if (!deliveryDescription) {
    return null;
  }

  const description = deliveryDescription.trim();

  // Handle specific Korean delivery terms
  if (description.includes('오늘')) {
    return 0; // Today
  }
  if (description.includes('내일')) {
    return 1; // Tomorrow
  }
  if (description.includes('모레')) {
    return 2; // Day after tomorrow
  }

  // Handle "도착 예정" with date patterns
  if (description.includes('도착 예정')) {
    // Try to extract date patterns like "12/25", "1/5" etc.
    const dateMatch = description.match(/(\d{1,2})\/(\d{1,2})/);
    if (dateMatch && scrapedAt) {
      try {
        const [, month, day] = dateMatch;

        // Ensure month and day are defined before using them
        if (!month || !day) {
          return null;
        }

        const scrapedDate = new Date(scrapedAt);
        const currentYear = scrapedDate.getFullYear();

        // Create target date
        let targetDate = new Date(currentYear, parseInt(month) - 1, parseInt(day));

        // If the target date is in the past, assume it's for next year
        if (targetDate < scrapedDate) {
          targetDate = new Date(currentYear + 1, parseInt(month) - 1, parseInt(day));
        }

        // Calculate difference in days
        const diffTime = targetDate.getTime() - scrapedDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays >= 0 ? diffDays : null;
      } catch (error) {
        console.warn('Error parsing delivery date:', error);
      }
    }
  }

  return null;
};

interface SSCpRematchingCoupangTableProps {
  selectedSkinSeoulProduct: SkinSeoulAdminProduct | null;
  cpSearchList: GpKeywordCpSearch[];
  loading?: boolean;
  onShowToast?: (message: string) => void;
  showFastDeliveryOnly: boolean;
  setShowFastDeliveryOnly: (value: boolean) => void;
}

export const SSCpRematchingCoupangTable: React.FC<SSCpRematchingCoupangTableProps> = ({
  selectedSkinSeoulProduct,
  cpSearchList,
  loading = false,
  onShowToast,
  showFastDeliveryOnly,
  setShowFastDeliveryOnly,
}) => {
  const [processingCopy, setProcessingCopy] = useState<Set<string>>(new Set());
  const [successfulCopies, setSuccessfulCopies] = useState<Set<string>>(new Set());

  // Filter products by delivery days (≤ 3 days) when toggle is enabled
  const filteredCpSearchList = cpSearchList.filter((product) => {
    if (!showFastDeliveryOnly) {
      return true; // Show all products when toggle is off
    }

    const deliveryDescription = product.cpSearchedProduct.shippingInfo?.deliveryDescription;
    if (deliveryDescription) {
      const deliveryDays = calculateDeliveryDays(deliveryDescription, product.createdAt);
      if (deliveryDays !== null && deliveryDays > 3) {
        return false; // Filter out products with delivery > 3 days
      }
    }
    return true; // Include products with delivery ≤ 3 days or no delivery info
  });

  // Helper function to copy to clipboard
  const copyToClipboard = useCallback(
    async (text: string, type: string) => {
      try {
        await navigator.clipboard.writeText(text);
        onShowToast?.(`${type} copied to clipboard: ${text}`);
        console.info(`📋 ${type} copied to clipboard:`, text);
      } catch (error) {
        console.error(`❌ Failed to copy ${type} to clipboard:`, error);
        onShowToast?.(`Failed to copy ${type} to clipboard`);
      }
    },
    [onShowToast],
  );

  // Handle updating product with Coupang information
  const handleUpdateProductWithCoupang = useCallback(
    async (product: GpKeywordCpSearch) => {
      if (!selectedSkinSeoulProduct) {
        onShowToast?.('No SkinSeoul product selected');
        return;
      }

      const productKey = `${product.cpProductId}-${product.cpItemId}`;
      setProcessingCopy((prev) => new Set(prev).add(productKey));

      try {
        const newCoupangUrl = buildCoupangProductUrl(product);

        const updateData: ProductAttributeUpdate = {
          productId: selectedSkinSeoulProduct.productId!,
          cpProductId: product.cpProductId,
          cpItemId: product.cpItemId,
          cpUrl: newCoupangUrl,
          cpNameKr: product.cpSearchedProduct.name ?? '',
        };

        const result = await productAttributesClient.updateProductAttributes(updateData);

        if (result.success) {
          setSuccessfulCopies((prev) => new Set(prev).add(productKey));
          onShowToast?.(result.message);
        } else {
          onShowToast?.(result.message);
        }
      } catch (error) {
        console.error('❌ Error in copy URL process:', error);
        onShowToast?.('Failed to copy URL and prepare sheet data');
      } finally {
        setProcessingCopy((prev) => {
          const newSet = new Set(prev);
          newSet.delete(productKey);
          return newSet;
        });
      }
    },
    [selectedSkinSeoulProduct, onShowToast],
  );

  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3">Loading Coupang products...</div>
        </Card.Body>
      </Card>
    );
  }

  if (!cpSearchList || cpSearchList.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Search size={48} className="text-muted mb-3" />
          <h5 className="text-muted">No products found</h5>
          <p className="text-muted">No Coupang products available for the selected SkinSeoul product</p>
        </Card.Body>
      </Card>
    );
  }

  if (filteredCpSearchList.length === 0 && showFastDeliveryOnly) {
    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Coupang Products (0 of {cpSearchList.length})</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Check
                type="checkbox"
                id="fast-delivery-filter-empty"
                label="Show 3 days delivery only"
                checked={showFastDeliveryOnly}
                onChange={(e) => setShowFastDeliveryOnly(e.target.checked)}
                className="mb-0"
              />
            </div>
          </div>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <Clock size={48} className="text-muted mb-3" />
          <h5 className="text-muted">No products with fast delivery</h5>
          <p className="text-muted">
            Found {cpSearchList.length} products, but none with delivery within 3 days.
            <br />
            <Button
              variant="link"
              size="sm"
              onClick={() => setShowFastDeliveryOnly(false)}
              className="p-0 text-decoration-underline"
            >
              Uncheck &quot;Show 3 days delivery only&quot; to see all products
            </Button>
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="coupang-products-table">
      <Card>
        <Card.Header>
          {/* Coupang Products Header */}
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">
                Coupang Products ({filteredCpSearchList.length}
                {cpSearchList.length !== filteredCpSearchList.length ? ` of ${cpSearchList.length}` : ''})
              </h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Check
                type="checkbox"
                id="fast-delivery-filter"
                label="Show 3 days delivery only"
                checked={showFastDeliveryOnly}
                onChange={(e) => setShowFastDeliveryOnly(e.target.checked)}
                className="mb-0"
              />
            </div>
          </div>
          {/* Selected Product Info */}
          {selectedSkinSeoulProduct && (
            <div className="mb-2 pb-2 border-bottom">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <small className="text-muted">
                    {selectedSkinSeoulProduct.nameKor && `Korean: ${selectedSkinSeoulProduct.nameKor} | `}
                    Brand: {selectedSkinSeoulProduct.brand} | SKU: {selectedSkinSeoulProduct.sku}
                  </small>
                  <br />
                  <a
                    href={`https://www.coupang.com/np/search?component=&q=${encodeURIComponent(selectedSkinSeoulProduct.attributes?.['cp_name_kr'] || selectedSkinSeoulProduct.nameKor || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary d-inline-flex align-items-center gap-1"
                    title={`Search on Coupang: ${selectedSkinSeoulProduct.attributes?.['cp_name_kr'] || selectedSkinSeoulProduct.nameKor || ''}`}
                    style={{ fontSize: '0.85rem' }}
                  >
                    🔍 Search on Coupang:{' '}
                    {selectedSkinSeoulProduct.attributes?.['cp_name_kr'] || selectedSkinSeoulProduct.nameKor}
                  </a>
                </div>
              </div>
            </div>
          )}
        </Card.Header>

        <Card.Body className="p-0" style={{ maxHeight: '82vh', overflowY: 'auto' }}>
          <div className="row g-2 p-2">
            {filteredCpSearchList.map((product) => {
              const productKey = `${product.cpProductId}-${product.cpItemId}`;
              const isProcessing = processingCopy.has(productKey);
              const isSuccessful = successfulCopies.has(productKey);
              const coupangUrl = buildCoupangProductUrl(product);

              // Check if this Coupang product is already matched to the selected SS product
              const isAlreadyMatched = selectedSkinSeoulProduct?.attributes?.['cp_product_code'] === product.cpUniqueId;

              return (
                <div key={productKey} className="col-md-6 col-lg-4">
                  <Card className="h-100 product-card">
                    <div className="position-relative">
                      {/* Product image */}
                      <Card.Img
                        variant="top"
                        src={product.cpSearchedProduct.thumbnailImageUrl}
                        alt={product.cpSearchedProduct.name}
                        style={{
                          height: '200px',
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(coupangUrl, '_blank', 'noopener,noreferrer')}
                      />

                      {/* Ranking badge */}
                      {product.cpSearchedProduct.ranking && (
                        <div className="ranking-ribbon">
                          <div className="ribbon-content" title={`Ranking: ${product.cpSearchedProduct.ranking}`}>
                            {product.cpSearchedProduct.ranking}
                            <div className="ribbon-tail" />
                            <div className="ribbon-shadow" />
                          </div>
                        </div>
                      )}
                    </div>

                    <Card.Body className="p-2">
                      {/* Product IDs */}
                      <div
                        className="d-flex align-items-center justify-content-center p-2 gap-1"
                        style={{
                          fontSize: '0.7rem',
                          color: '#6c757d',
                        }}
                      >
                        <span>
                          {product.cpProductId}-{product.cpItemId}
                        </span>
                        <button
                          className="btn btn-sm p-0 border-0 bg-transparent"
                          style={{
                            color: '#6c757d',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            opacity: 0.7,
                            transition: 'opacity 0.2s',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(`${product.cpProductId}-${product.cpItemId}`, 'Product ID');
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.7';
                          }}
                          title="Copy product ID"
                        >
                          <Copy size={12} />
                        </button>
                      </div>

                      {/* Brand */}
                      <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                        {product.cpSearchedProduct.brand}
                      </div>

                      {/* Product Name - Full Text */}
                      <div
                        className="fw-normal mb-1"
                        style={{
                          fontSize: '0.85rem',
                          lineHeight: '1.2',
                          height: '3.1rem',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                        title={product.cpSearchedProduct.name}
                      >
                        {product.cpSearchedProduct.name}
                      </div>

                      {/* Rating and Reviews */}
                      {product.cpSearchedProduct.rating && product.cpSearchedProduct.rating > 0 && (
                        <div className="d-flex align-items-center mb-1">
                          <div className="d-flex align-items-center me-1">
                            <Star size={12} className="text-warning me-1" fill="currentColor" />
                            <span style={{ fontSize: '0.75rem' }}>{product.cpSearchedProduct.rating}</span>
                          </div>
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                            ({product.cpSearchedProduct.reviews?.toLocaleString()})
                          </span>
                        </div>
                      )}

                      {/* Price with Shipping Icon */}
                      <div className="mt-auto">
                        <div className="d-flex flex-column">
                          {/* Product Options/Size */}
                          {product.cpSearchedProduct.options && product.cpSearchedProduct.options.length > 0 && (
                            <div className="mb-1">
                              <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                                {product.cpSearchedProduct.options[0]?.itemName}
                              </span>
                            </div>
                          )}

                          {/* Price and Unit Price */}
                          <div className="d-flex flex-column mb-1">
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex flex-column">
                                <span className="text-danger fw-bold" style={{ fontSize: '0.9rem' }}>
                                  {formatPrice(product.cpSearchedProduct)}
                                  {product.cpSearchedProduct.priceInfo?.unitPrice && (
                                    <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                      {product.cpSearchedProduct.priceInfo.unitPrice}
                                    </span>
                                  )}
                                </span>
                                {(product.cpSearchedProduct.priceInfo?.discountRate ||
                                  (product.cpSearchedProduct as any).discountRate) && (
                                  <span className="text-success" style={{ fontSize: '0.6rem' }}>
                                    {product.cpSearchedProduct.priceInfo?.discountRate ||
                                      (product.cpSearchedProduct as any).discountRate}{' '}
                                    할인
                                  </span>
                                )}
                              </div>

                              {/* Shipping Icon */}
                              {(product.cpSearchedProduct.shippingInfo?.shippingType ||
                                product.cpSearchedProduct.shipping) && (
                                <div className="d-flex align-items-center ms-2">
                                  {product.cpSearchedProduct.shippingInfo?.shippingType ? (
                                    // New shippingInfo structure
                                    <>
                                      {product.cpSearchedProduct.shippingInfo.shippingType === ShippingType.ROCKET && (
                                        <img
                                          src="/assets/rocket-logo.png"
                                          alt="Rocket"
                                          style={{ width: 'auto', height: '14px' }}
                                        />
                                      )}
                                      {product.cpSearchedProduct.shippingInfo.shippingType ===
                                        ShippingType.SELLER_ROCKET && (
                                        <img
                                          src="/assets/seller-rocket-logo.png"
                                          alt="Seller Rocket"
                                          style={{ height: '14px', width: 'auto' }}
                                          title="판매자로켓"
                                        />
                                      )}
                                    </>
                                  ) : (
                                    // Legacy shipping field
                                    <>
                                      {product.cpSearchedProduct.shipping === 'Rocket' && (
                                        <img
                                          src="/assets/rocket-logo.png"
                                          alt="Rocket"
                                          style={{ width: 'auto', height: '14px' }}
                                        />
                                      )}
                                      {product.cpSearchedProduct.shipping === 'Seller Rocket' && (
                                        <img
                                          src="/assets/seller-rocket-logo.png"
                                          alt="Seller Rocket"
                                          style={{ height: '14px', width: 'auto' }}
                                          title="판매자로켓"
                                        />
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Delivery Information */}
                          <div className="d-flex flex-column" style={{ fontSize: '0.65rem' }}>
                            {product.cpSearchedProduct.shippingInfo?.deliveryDescription && (
                              <div className="d-flex align-items-center gap-1">
                                <span className="text-success" style={{ fontSize: '0.7rem' }}>
                                  {product.cpSearchedProduct.shippingInfo.deliveryDescription}
                                </span>
                                {(() => {
                                  const deliveryDays = calculateDeliveryDays(
                                    product.cpSearchedProduct.shippingInfo.deliveryDescription!,
                                    product.createdAt || product.updatedAt,
                                  );
                                  if (deliveryDays !== null) {
                                    // Green for 0-3 days (today to 3 days), red for more than 3 days
                                    const badgeColor =
                                      deliveryDays >= 0 && deliveryDays <= 3 ? 'bg-success' : 'bg-danger';
                                    return (
                                      <span className={`badge ${badgeColor} text-white`} style={{ fontSize: '0.6rem' }}>
                                        {deliveryDays} days
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            )}
                            {product.cpSearchedProduct.shippingInfo?.shippingType === ShippingType.SELLER_DELIVERY && (
                              <span className="text-muted">무료배송</span>
                            )}
                            {product.cpSearchedProduct.shipping === 'Standard' && (
                              <span className="text-success fw-bold">무료배송</span>
                            )}
                            {product.cpSearchedProduct.shipping !== 'Rocket' &&
                              product.cpSearchedProduct.shipping !== 'Seller Rocket' &&
                              product.cpSearchedProduct.shipping !== 'Standard' &&
                              product.cpSearchedProduct.shipping && (
                                <span className="text-muted">{product.cpSearchedProduct.shipping}</span>
                              )}
                          </div>
                        </div>
                      </div>

                      {/* Bottom section with copy URL action - replaces matching info */}
                      <div className="mt-2 pt-2 border-top">
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2">
                            {/* Copy URL Button - replaces checkbox */}
                            <div className="d-flex align-items-center" style={{ height: '24px' }}>
                              <Button
                                variant={
                                  isProcessing ? 'warning' : isSuccessful || isAlreadyMatched ? 'success' : 'primary'
                                }
                                size="sm"
                                disabled={isProcessing || isAlreadyMatched}
                                onClick={() => handleUpdateProductWithCoupang(product)}
                                className="d-flex align-items-center gap-1"
                                style={{
                                  fontSize: '0.75rem',
                                  height: '24px',
                                  lineHeight: '1.2',
                                  padding: '0 8px',
                                  fontWeight: isAlreadyMatched || isSuccessful ? '600' : 'normal',
                                  opacity: isAlreadyMatched ? '0.9' : '1',
                                  textRendering: 'optimizeLegibility',
                                  WebkitFontSmoothing: 'antialiased',
                                  MozOsxFontSmoothing: 'grayscale',
                                }}
                                title={
                                  isProcessing
                                    ? 'Processing...'
                                    : isAlreadyMatched
                                      ? 'This product is already matched'
                                      : isSuccessful
                                        ? 'Product updated successfully'
                                        : 'Update SkinSeoul product with Coupang information'
                                }
                              >
                                {isProcessing ? (
                                  <>
                                    <Spinner size="sm" style={{ width: '10px', height: '10px' }} />
                                    Updating...
                                  </>
                                ) : isAlreadyMatched ? (
                                  <>✓ Updated</>
                                ) : isSuccessful ? (
                                  <>✓ Updated</>
                                ) : (
                                  <>
                                    <Clipboard size={10} />
                                    Update Product
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SSCpRematchingCoupangTable;
